import { Typography } from 'antd'
import { motion, useReducedMotion } from 'motion/react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import type { OrganizationLong } from 'src/api/main'
import {
  clearWorkspaceWelcome,
  peekWorkspaceWelcome
} from 'src/features/workspaces'

import { useStyles } from './styles'
import {
  formatWorkspaceLabel,
  resolveActiveWorkspace,
  workspaceAccent,
  workspaceInitials,
  WORKSPACE_TILE_ID
} from './utils'

const { Text, Title } = Typography

/** How long the welcome holds before it leaves on its own. */
const HOLD_MS = 1200

/** Duration of the flight and the backdrop fade — the system's `slow` step. */
const LEAVE_MS = 240

const EASE = [0.2, 0.8, 0.2, 1] as const

type Rect = { x: number; y: number; width: number }

type Claim = { label: string; accent: string; photoUrl?: string }

type Props = {
  workspaces: OrganizationLong[]
  activeWorkspaceId: number | undefined
}

/**
 * One-time welcome shown the first time a freshly created workspace is opened.
 *
 * Rendered into a portal over the shell rather than as a route, so the
 * workspace behind it is genuinely loading while it plays. It leaves by flying
 * its tile into the sidebar's workspace tile — the point is to hand the user
 * their workspace, not to show a splash screen. Claimed from
 * `takeWorkspaceWelcome`, which reads and clears in one call, so a remount
 * cannot replay it.
 */
export const WorkspaceWelcomeOverlay = ({
  workspaces,
  activeWorkspaceId
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()

  const anchorRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<'visible' | 'leaving' | 'hidden'>(
    'visible'
  )
  const [origin, setOrigin] = useState<Rect | null>(null)
  const [target, setTarget] = useState<Rect | null>(null)

  const active = resolveActiveWorkspace(workspaces, activeWorkspaceId)

  /**
   * Decided on the first render rather than from an effect: even a layout
   * effect commits a frame first, and a flash of the very screen this overlay
   * exists to introduce is worse than no animation at all.
   *
   * The label and accent are captured here and never re-read — the session
   * refresh that follows creation rewrites `workspaces`, and a name changing
   * halfway through the animation would be visible. The shell only mounts this
   * once it has a workspace, so there is always something to read.
   */
  const [claim] = useState<Claim | null>(() =>
    active && peekWorkspaceWelcome(active.id)
      ? {
          label: formatWorkspaceLabel(active),
          accent: workspaceAccent(formatWorkspaceLabel(active)),
          // The workspace was just created with a picture; handing it over under
          // its initial instead is the one frame where it does not look like the
          // thing that was set up.
          photoUrl: active.photo?.file
        }
      : null
  )

  // Spent once the welcome is on screen, so a reload mid-animation does not
  // replay it. Kept out of the initialiser above, which StrictMode invokes
  // twice — the second call would find the marker already gone.
  useEffect(() => {
    if (claim && active) clearWorkspaceWelcome(active.id)
  }, [claim, active])

  const showing = claim !== null && phase !== 'hidden'
  const visible = showing && phase === 'visible'
  const leaving = showing && phase === 'leaving'

  // Where the tile starts: the hidden placeholder inside the centred column, so
  // the tile lines up with the text without either side hard-coding a layout.
  useLayoutEffect(() => {
    if (!visible || !anchorRef.current) return

    const rect = anchorRef.current.getBoundingClientRect()
    setOrigin({ x: rect.left, y: rect.top, width: rect.width })
  }, [visible])

  /**
   * The sidebar tile is measured here rather than after the switch, so the
   * flight has its destination on the very first frame of the transition — it
   * only reaches its final position once the shell has laid itself out, which
   * is why this cannot be done on mount.
   */
  const leave = useCallback(() => {
    const element = document.getElementById(WORKSPACE_TILE_ID)
    if (element) {
      const rect = element.getBoundingClientRect()
      setTarget({ x: rect.left, y: rect.top, width: rect.width })
    }
    setPhase((current) => (current === 'visible' ? 'leaving' : current))
  }, [])

  useEffect(() => {
    if (!visible) return

    const leaveId = window.setTimeout(leave, HOLD_MS)

    return () => window.clearTimeout(leaveId)
  }, [visible, leave])

  // Unmount on a timer of its own rather than off an animation callback: a
  // dropped frame or an interrupted transition must not leave the shell behind
  // a backdrop the user cannot dismiss.
  useEffect(() => {
    if (!leaving) return

    const doneId = window.setTimeout(() => setPhase('hidden'), LEAVE_MS)

    return () => window.clearTimeout(doneId)
  }, [leaving])

  useEffect(() => {
    if (!visible) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') leave()
    }
    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible, leave])

  if (!showing) return null

  const flying = leaving && target && !reduceMotion
  const transition = { duration: LEAVE_MS / 1000, ease: EASE }

  return createPortal(
    <div
      className={styles.welcomeRoot}
      role="presentation"
      onClick={leave}
      aria-label={t('shell.welcome.greeting')}
    >
      {/* Opaque from its first frame — it only fades on the way out. Fading it
          in would show the workspace through it for the whole duration. */}
      <motion.div
        className={styles.welcomeBackdrop}
        initial={{ opacity: 1 }}
        animate={{ opacity: leaving ? 0 : 1 }}
        transition={transition}
      />

      <div className={styles.welcomeColumn}>
        {/* Placeholder the real tile is measured against and drawn over. */}
        <div ref={anchorRef} className={styles.welcomeTileAnchor} aria-hidden />

        <motion.div
          className={styles.welcomeText}
          initial={{ opacity: 0, y: reduceMotion ? 0 : 2 }}
          animate={{ opacity: leaving ? 0 : 1, y: 0 }}
          transition={{
            ...transition,
            delay: leaving || reduceMotion ? 0 : 0.08
          }}
        >
          <Text type="secondary">{t('shell.welcome.greeting')}</Text>
          <Title level={2} style={{ margin: 0 }}>
            {claim.label}
          </Title>
        </motion.div>
      </div>

      {origin ? (
        <motion.div
          className={cx(
            styles.welcomeTile,
            claim.photoUrl && styles.welcomeTilePhoto
          )}
          // Flat like the sidebar tile it lands on — the flight only reads as
          // "this becomes that" if the two are pixel-for-pixel the same fill.
          style={{
            background: claim.photoUrl ? undefined : claim.accent
          }}
          initial={{ x: origin.x, y: origin.y, scale: 1, opacity: 0 }}
          animate={
            flying
              ? {
                  x: target.x,
                  y: target.y,
                  scale: target.width / origin.width,
                  opacity: 1
                }
              : {
                  x: origin.x,
                  y: origin.y,
                  scale: 1,
                  opacity: leaving ? 0 : 1
                }
          }
          transition={transition}
          aria-hidden
        >
          {claim.photoUrl ? (
            <img
              src={claim.photoUrl}
              alt=""
              className={styles.welcomeTileImage}
            />
          ) : (
            workspaceInitials(claim.label)
          )}
        </motion.div>
      ) : null}
    </div>,
    document.body
  )
}
